import type { VerifiedMemberDetail } from "@/api/giftcard.api";
import { memberProfile as defaultProfile, memberStats as defaultStats } from "./mockData";
import type { MemberProfile, MemberStat } from "./types";

export interface MappedVerifiedMember {
  profile: MemberProfile;
  stats: MemberStat[];
}

function getAvatarInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "M";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function formatDisplayDate(value?: string, fallback = "") {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/** Format as plain digits only — strips any currency symbol from API/mock values. */
function formatAmount(value: number | string | undefined) {
  if (value === undefined || value === null || value === "") return "0";

  const numeric =
    typeof value === "number"
      ? value
      : Number(String(value).replace(/[^\d.-]/g, ""));

  if (Number.isNaN(numeric)) return "0";
  return numeric.toLocaleString("en-IN");
}

/**
 * Map member detail (VerifyOTP `memberDetails[0]` or GetMemberDetails `newMemberDetails[0]`)
 * onto dashboard profile + stats cards.
 */
export function mapVerifiedMemberDetails(
  detail: VerifiedMemberDetail | undefined,
  mobileNo: string,
  existing?: Partial<MemberProfile>
): MappedVerifiedMember {
  const base = { ...defaultProfile, ...existing };
  const name = detail?.name?.trim() || base.name;

  const profile: MemberProfile = {
    ...base,
    name,
    mobile: mobileNo || base.mobile,
    dob: formatDisplayDate(detail?.dob, base.dob),
    doa: formatDisplayDate(detail?.doa, base.doa),
    gender: detail?.gender?.trim() || base.gender,
    city: detail?.city?.trim() || base.city,
    avatarInitials: getAvatarInitials(name),
  };

  const stats: MemberStat[] = defaultStats.map((stat) => {
    if (stat.id === "transactions") {
      return {
        ...stat,
        value: formatAmount(detail?.totalTxn),
      };
    }
    if (stat.id === "spent") {
      return {
        ...stat,
        value: formatAmount(detail?.spend),
      };
    }
    if (stat.id === "points") {
      return {
        ...stat,
        value: formatAmount(detail?.pointsBalance),
      };
    }
    return stat;
  });

  return { profile, stats };
}
